const BILL_DOCUMENT_UPLOAD_LIMIT_BYTES = 50 * 1024 * 1024;

const SUPPORTED_BILL_DOCUMENT_TYPES: Record<string, readonly string[]> = {
  "application/pdf": ["pdf"],
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/webp": ["webp"],
};

export const BILL_DOCUMENT_INPUT_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp";
export const BILL_DOCUMENT_HELP_TEXT =
  "PDF, PNG, JPEG, or WebP up to 50MB. A bill holds at most 10 documents.";
export const BILL_DOCUMENT_INVALID_TYPE_MESSAGE =
  "Only PDF, PNG, JPEG, and WebP documents are accepted.";
export const BILL_DOCUMENT_TOO_LARGE_MESSAGE =
  "Document is too large. Please choose a file no larger than 50MB.";
export const BILL_DOCUMENT_EMPTY_MESSAGE = "Document is empty.";

function resolveBillDocumentContentType(file: File): string | null {
  const contentType = file.type.trim().toLowerCase();
  if (SUPPORTED_BILL_DOCUMENT_TYPES[contentType]) return contentType;

  const name = file.name.trim().toLowerCase();
  for (const [type, extensions] of Object.entries(
    SUPPORTED_BILL_DOCUMENT_TYPES,
  )) {
    if (extensions.some((extension) => name.endsWith(`.${extension}`))) {
      return type;
    }
  }
  return null;
}

export function validateBillDocumentFile(file: File): string | null {
  if (!resolveBillDocumentContentType(file)) {
    return BILL_DOCUMENT_INVALID_TYPE_MESSAGE;
  }
  if (file.size <= 0) {
    return BILL_DOCUMENT_EMPTY_MESSAGE;
  }
  if (file.size > BILL_DOCUMENT_UPLOAD_LIMIT_BYTES) {
    return BILL_DOCUMENT_TOO_LARGE_MESSAGE;
  }
  return null;
}

export interface BillDocumentUploadTarget {
  uploadUrl: string;
  key: string;
  contentType: string;
  ifNoneMatch: "*";
  uploadToken: string;
}

interface UploadBillDocumentDirectlyOptions {
  billId: string;
  file: File;
  createUploadTarget: (args: {
    billId: string;
    filename: string;
    contentType: string;
    fileSize: number;
  }) => Promise<BillDocumentUploadTarget>;
  finalizeUpload: (args: {
    billId: string;
    key: string;
    filename: string;
    uploadToken: string;
  }) => Promise<unknown>;
}

export async function uploadBillDocumentDirectly({
  billId,
  file,
  createUploadTarget,
  finalizeUpload,
}: UploadBillDocumentDirectlyOptions): Promise<void> {
  const validationError = validateBillDocumentFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const contentType = resolveBillDocumentContentType(file);
  if (!contentType) {
    throw new Error(BILL_DOCUMENT_INVALID_TYPE_MESSAGE);
  }

  const filename = file.name.trim() || "bill-document.pdf";
  const target = await createUploadTarget({
    billId,
    filename,
    contentType,
    fileSize: file.size,
  });

  const response = await fetch(target.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": target.contentType,
      "If-None-Match": target.ifNoneMatch,
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Document upload failed with status ${response.status}`);
  }

  await finalizeUpload({
    billId,
    key: target.key,
    filename,
    uploadToken: target.uploadToken,
  });
}
